<template>
  <div class="perm-placement-form" :class="{ history: isHistoryRecord }">
    <form>
      <section>
        <h1>{{ $t('permPlacement.labels.candidatePlaced') }}</h1>
        <div class="form-control">
          <label
            >{{ $t('permPlacement.labels.invoiceNum')
            }}<span v-if="formData.status === 'SUBMITTED'" class="asterisk">*</span></label
          >
          <InputField
            v-model="formData.invoice_number"
            type="text"
            class="bordered"
            :class="{ error: formErrors['Invoice Number'] }"
            field-icon="barcode"
          />
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('permPlacement.labels.lastName') }}<span class="asterisk">*</span></label>
            <InputField
              v-model="formData.last_name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['Last Name'] }"
            />
          </div>

          <div class="form-control">
            <label
              >{{ $t('permPlacement.labels.firstName')
              }}<span v-show="!formData.occupation" class="asterisk">*</span></label
            >
            <InputField
              v-model="formData.first_name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['First Name'] }"
            />
          </div>
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.occupation') }}</label>
          <InputField
            v-model="formData.occupation"
            type="text"
            class="bordered"
            :class="{ error: formErrors['Occupation'] }"
          />
        </div>
      </section>

      <section>
        <h1>{{ $t('permPlacement.labels.clientDetails') }}</h1>
        <h2>{{ formData.client_name }}</h2>
        <div
          v-if="!formData.client_name"
          class="form-control"
          :class="{ error: formErrors['Client'] }"
        >
          <span class="asterisk">*</span>
          <TolTypeAhead
            v-show="!formData.clientName"
            :search-params="{
              url: '/clients/client-quick-search',
              searchKeys: ['name']
            }"
            class="bordered"
            :menu-label="'Search client name'"
            @select-item="onClientSelect($event)"
          >
            <template v-slot="{ value }">
              {{ value.name }}
            </template>
          </TolTypeAhead>
        </div>

        <div class="break" />
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.salary') }}<span class="asterisk">*</span></label>
          <InputField
            :value="formData.salary"
            type="text"
            class="bordered"
            :class="{ error: formErrors['Salary'] }"
            field-icon="dollar-sign"
            @input="updateRelatedValues($event, 'salary')"
          />
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.findersPercentage') }}</label>
          <InputField
            :value="formData.finders_percentage"
            type="text"
            class="bordered"
            picker="percent"
            @input="updateRelatedValues($event, 'finders_percentage')"
          />
        </div>
        <div class="form-group">
          <div class="form-control">
            <label
              >{{ $t('permPlacement.labels.billingAmount') }}<span class="asterisk">*</span></label
            >
            <InputField
              :value="formData.billing_amount"
              type="text"
              class="bordered"
              :class="{ error: formErrors['Billing Amount'] }"
              field-icon="dollar-sign"
              @input="updateRelatedValues($event, 'billing_amount')"
            />
          </div>

          <div class="form-control currency">
            <label>{{ $t('permPlacement.labels.currency') }}<span class="asterisk">*</span></label>
            <DropdownSelect
              :menu-items="currencies"
              :selected-item="formData.currency_code"
              class="bordered form-currency-input"
              @select-item="
                formData.currency_code = $event;
                isOnScreenDataModified = true;
              "
            />
          </div>
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.taxesOnInvoice') }}</label>
          <div>
            <checkbox
              size="tiny"
              :checked="formData.gst_on_invoice"
              @change="
                formData.gst_on_invoice = !formData.gst_on_invoice;
                isOnScreenDataModified = true;
              "
            />
            {{ $t('permPlacement.labels.gst') }} &nbsp;
            <checkbox
              size="tiny"
              :checked="formData.hst_on_invoice"
              @change="
                formData.hst_on_invoice = !formData.hst_on_invoice;
                isOnScreenDataModified = true;
              "
            />
            {{ $t('permPlacement.labels.hst') }} &nbsp;
            <checkbox
              size="tiny"
              :checked="formData.qst_on_invoice"
              @change="
                formData.qst_on_invoice = !formData.qst_on_invoice;
                isOnScreenDataModified = true;
              "
            />
            {{ $t('permPlacement.labels.qst') }}
          </div>
        </div>

        <div class="break" />

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.billTo') }}</label>
          <tol-type-ahead
            v-if="!formData.billingMethod"
            :search-params="{
              url: '/assignments/billing-method-lookup',
              searchKeys: [
                'attention',
                'other',
                'billing_methods.email',
                'users_view.email',
                'line1',
                'line2',
                'city',
                'postal_code',
                'province_abbr'
              ],
              client_id: formData.client_id
            }"
            class="bordered"
            @select-item="onSelectBillingMethodSearchItem($event, 'BILLING_METHOD')"
          >
            <template v-slot="{ value }">
              <billing-method-display :data="value" :show-remove="false" />
            </template>
          </tol-type-ahead>
          <billing-method-display
            v-else
            :data="formData.billingMethod"
            @remove="removeOnScreenValue('BILLING_METHOD')"
          />
          <span class="error">{{ errors.first('Bill to') }} </span>
        </div>

        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('permPlacement.labels.paymentTerm') }}</label>
            <InputField
              :value="
                selectedClientPaymentTerm ? selectedClientPaymentTerm : formData.payment_term_amount
              "
              type="text"
              class="bordered"
              @input="checkPaymentTerms($event)"
            />
          </div>
          <div class="form-control term">
            <DropdownSelect
              :menu-items="termUnits"
              :selected-item="selectedClientPaymentTermUnit"
              class="bordered"
              @select-item="
                formData.payment_term_unit = $event;
                checkPaymentTerms();
              "
            />
          </div>
        </div>
      </section>

      <section>
        <h1>{{ $t('permPlacement.labels.outsourceAndSplit') }}</h1>
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.outsourceAmount') }}</label>
          <InputField
            v-model="formData.outsource_amount"
            type="text"
            class="bordered"
            field-icon="dollar-sign"
          />
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.outsourcePaidTo') }}</label>
          <InputField v-model="formData.outsource_person" type="text" class="bordered" />
        </div>

        <div class="form-group">
          <div class="form-control sm">
            <label>{{ $t('permPlacement.labels.tesShareOfSplit') }}</label>
            <InputField
              v-model="splitPercentage"
              type="text"
              class="bordered"
              picker="percent"
              readonly
            />
          </div>
          <div class="form-control empty">
            <label>{{ $t('permPlacement.labels.or') }}</label>
          </div>
          <div class="form-control sm">
            <label>&nbsp;</label>
            <InputField
              v-model="splitDollars"
              type="text"
              class="bordered"
              field-icon="dollar-sign"
              readonly
            />
          </div>
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.splitDescription') }}</label>
          <textarea v-model="formData.split_description" rows="3" cols="30" />
        </div>
      </section>

      <section>
        <h1>{{ $t('permPlacement.labels.guarantee') }}</h1>
        <div class="form-control" :class="{ error: formErrors['Guarantee With Replacement'] }">
          <label
            >{{ $t('permPlacement.labels.withReplacement') }}<span class="asterisk">*</span></label
          >
          <div class="multi-inline">
            <TolRadioButton
              button-label="Yes"
              label="true"
              name="guaranteeReplacement"
              :value="formData.guarantee_replacement"
              @toggle="formData.guarantee_replacement = true"
            />
            <TolRadioButton
              button-label="No"
              label="false"
              name="guaranteeReplacement"
              :value="formData.guarantee_replacement"
              @toggle="formData.guarantee_replacement = false"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('permPlacement.labels.replacementTerm') }}</label>
            <InputField v-model="guaranteeAmount" type="text" class="bordered" />
          </div>
          <div class="form-control term">
            <DropdownSelect
              :menu-items="termUnits"
              :selected-item="guaranteeUnit"
              class="bordered"
              :disabled="
                !formData.guarantee_replacement || !guaranteeAmount || guaranteeAmount === '0'
              "
              @select-item="formData.guarantee_unit = $event"
            />
          </div>
        </div>

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.otherAgreement') }}</label>
          <textarea v-model="formData.guarantee_other" rows="3" cols="30" />
        </div>
      </section>

      <section>
        <h1>Stakeholders</h1>
        <div class="form-control" :class="{ error: accountManagerFieldHasError }">
          <tol-list :list-items="permPlacementAccountManagers">
            <template v-slot:list-head>
              <div class="list-header" @click="toggleTypeAhead('ACCOUNT_MANAGER')">
                {{ $t('permPlacement.labels.accountManagers') }}
                <font-awesome-icon icon="user-plus" />
              </div>
              <tol-type-ahead
                v-show="typeAhead.ACCOUNT_MANAGER"
                :search-params="{
                  url: '/assignments/user-quick-search?excludeNullCommissions=true',
                  searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                  objectType: 'tesEmployee'
                }"
                class="blue"
                @select-item="onSelectSearchItem($event, 'ACCOUNT_MANAGER')"
              >
                <template v-slot="{ value }">
                  {{ value.last_name }}, {{ value.first_name }} #
                  {{ value.commission_number }}
                </template>
              </tol-type-ahead>
            </template>
            <template v-slot:body="{ value }">
              <div class="list-item-label">
                {{ value.display_name }} #{{ value.commission_number }} @
              </div>
              <InputField
                v-model="value.split"
                type="text"
                class="bordered form-number-input"
                picker="percent"
              />
              <font-awesome-icon
                icon="minus-circle"
                @click="removeCommissionUser(value.commission_profile_id, 'ACCOUNT_MANAGER')"
              >
              </font-awesome-icon>
            </template>
          </tol-list>
          <div
            v-if="hasErrorKey('ACCOUNT_MANAGER')"
            class="error-div"
            @click="clearClientValidation"
          >
            <font-awesome-icon icon="exclamation-circle" />{{ getErrorKey('ACCOUNT_MANAGER') }}
          </div>
        </div>

        <div class="form-control" :class="{ error: recruiterFieldHasError }">
          <tol-list :list-items="permPlacementRecruiters">
            <template v-slot:list-head>
              <div class="list-header" @click="toggleTypeAhead('RECRUITER')">
                {{ $t('permPlacement.labels.recruiters') }}
                <font-awesome-icon icon="user-plus" />
              </div>

              <tol-type-ahead
                v-show="typeAhead.RECRUITER"
                :search-params="{
                  url: '/assignments/user-quick-search?excludeNullCommissions=true',
                  searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                  objectType: 'tesEmployee'
                }"
                class="blue"
                @select-item="onSelectSearchItem($event, 'RECRUITER')"
              >
                <template v-slot="{ value }">
                  {{ value.last_name }}, {{ value.first_name }} #
                  {{ value.commission_number }}
                </template>
              </tol-type-ahead>
            </template>
            <template v-slot:body="{ value }">
              <div class="list-item-label">
                {{ value.display_name }} #{{ value.commission_number }} @
              </div>
              <InputField
                v-model="value.split"
                type="text"
                class="bordered form-number-input"
                picker="percent"
              />
              <font-awesome-icon
                icon="minus-circle"
                @click="removeCommissionUser(value.commission_profile_id, 'RECRUITER')"
              >
              </font-awesome-icon>
            </template>
          </tol-list>
        </div>

        <div class="form-control">
          <tol-list :list-items="permPlacementCommissionOverrides">
            <template v-slot:list-head>
              <div class="list-header" @click="toggleTypeAhead('OVERRIDE')">
                {{ $t('permPlacement.labels.commissionOverrides') }}
                <font-awesome-icon icon="user-plus" />
              </div>
              <tol-type-ahead
                v-show="typeAhead.OVERRIDE"
                :search-params="{
                  url: '/assignments/user-quick-search?excludeNullCommissions=true',
                  searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                  objectType: 'tesEmployee'
                }"
                class="blue"
                @select-item="onSelectSearchItem($event, 'OVERRIDE')"
              >
                <template v-slot="{ value }">
                  {{ value.last_name }}, {{ value.first_name }} #
                  {{ value.commission_number }}
                </template>
              </tol-type-ahead>
            </template>
            <template v-slot:body="{ value }">
              {{ value.display_name }} #{{ value.commission_number }} @
              <InputField
                v-model="value.split"
                type="text"
                class="bordered form-number-input"
                picker="percent"
              />
              <font-awesome-icon
                icon="minus-circle"
                @click="removeCommissionUser(value.commission_profile_id, 'OVERRIDE')"
              >
              </font-awesome-icon>
            </template>
          </tol-list>
        </div>
      </section>

      <section>
        <h1>{{ $t('permPlacement.labels.misc') }}</h1>
        <div class="form-control" :class="{ error: formErrors['Branch'] }">
          <label>{{ $t('permPlacement.labels.branch') }}<span class="asterisk">*</span></label>
          <DropdownSelect
            :menu-items="branchNames"
            :selected-item="formData.branch_name"
            class="bordered"
            @select-item="formData.branch_name = $event"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.division') }}<span class="asterisk">*</span></label>
          <DropdownSelect
            :menu-items="divisionAbbrs.map(d => d.abbr + ' ' + d.name)"
            :selected-item="formData.division_abbr"
            class="bordered"
            :class="{ error: formErrors['Division'] }"
            @select-item="formData.division_abbr = $event"
          />
        </div>
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.startDate') }}<span class="asterisk">*</span></label>
          <form-date-picker
            v-model="startDate"
            :type="'bordered'"
            :class="{ error: formErrors['Start Date'] }"
            @change="onStartDateChanged($event)"
          />
          <div v-if="hasErrorKey('Start Date')" class="error-div">
            <font-awesome-icon icon="exclamation-circle" />{{ getErrorKey('start_date') }}
          </div>
        </div>
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.clientTransfer') }}</label>
          <div class="multi-inline">
            <TolRadioButton
              button-label="Yes"
              label="true"
              name="clientTransfer"
              :value="formData.client_transfer"
              @toggle="formData.client_transfer = true"
            />
            <TolRadioButton
              button-label="No"
              label="false"
              name="clientTransfer"
              :value="formData.client_transfer"
              @toggle="formData.client_transfer = false"
            />
          </div>
        </div>
        <div class="form-control">
          <label>{{ $t('permPlacement.labels.originalStartDate') }}</label>
          <form-date-picker
            v-show="formData.client_transfer"
            v-model="originalStartDate"
            :type="'bordered'"
            @change="onOriginalStartDateChanged($event)"
          />
          <span v-show="!formData.client_transfer" class="grey">n/a</span>
          <div v-if="hasErrorKey('original_start_date')" class="error-div">
            <font-awesome-icon icon="exclamation-circle" />{{ getErrorKey('original_start_date') }}
          </div>
        </div>

        <div class="break" />

        <div class="form-control">
          <label>{{ $t('permPlacement.labels.comments') }}</label>
          <textarea v-model="formData.comments" rows="3" cols="30" />
        </div>
      </section>

      <div v-if="!isHistoryRecord" class="module-action-container">
        <div>
          <tol-button
            v-if="canThisUserSubmit"
            button-type="minor"
            class="module-action submit"
            icon-name="upload"
            :tooltip="$t('permPlacement.actionButtons.submit')"
            :disabled="formData.status === 'SUBMITTED' || !formData.reference_number"
            @click="doPermPlacementAction('SUBMIT')"
          >
          </tol-button>
          <tol-button
            v-if="canThisUserProcess"
            button-type="minor"
            class="module-action process"
            icon-name="check"
            :tooltip="$t('permPlacement.actionButtons.process')"
            :disabled="formData.status === 'PROCESSED' || !formData.reference_number"
            @click="doPermPlacementAction('PROCESS')"
          >
          </tol-button>

          <tol-button
            v-if="canThisUserReject"
            button-type="minor"
            class="module-action reject"
            icon-name="times"
            :tooltip="$t('permPlacement.actionButtons.reject')"
            :disabled="formData.status === 'REJECTED' || !formData.reference_number"
            @click="doPermPlacementAction('REJECT')"
          >
          </tol-button>

          <tol-button
            v-if="canThisUserCancel"
            button-type="minor"
            class="module-action reject"
            icon-name="times"
            :tooltip="$t('permPlacement.actionButtons.cancel')"
            :disabled="formData.status === 'CANCELLED' || !formData.reference_number"
            @click="doPermPlacementAction('CANCEL')"
          >
          </tol-button>

          <tol-button
            v-if="canThisUserObsolete"
            button-type="minor"
            class="module-action obsolete"
            icon-name="ban"
            :tooltip="$t('permPlacement.actionButtons.obsolete')"
            :disabled="formData.status === 'OBSOLETE' || !formData.reference_number"
            @click="doPermPlacementAction('OBSOLETE')"
          >
          </tol-button>
          <tol-button
            v-if="canThisUserReopen"
            button-type="minor"
            class="module-action reopen"
            icon-name="undo"
            :tooltip="$t('permPlacement.actionButtons.reopen')"
            :disabled="formData.status === 'DRAFT' || !formData.reference_number"
            @click="doPermPlacementAction('REOPEN')"
          >
          </tol-button>
        </div>
        <div class="comment">
          <InputField
            v-model="formData.created_comment"
            type="text"
            class="bordered"
            :class="{ error: formErrors['Created comment'] && !formData.created_comment }"
            field-icon="comments"
          />
        </div>
        <div>
          <tol-button
            v-if="canThisUserSave"
            id="save-btn"
            button-type="save"
            label="Save"
            :class="[
              'module-action',
              { disabled: !isOnScreenDataModified && formData.reference_number }
            ]"
            @click="doPermPlacementAction('SAVE')"
          >
          </tol-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import TolRadioButton from '@/app/components/TolRadioButton';
import TolList from '@/app/components/TolList';
import Checkbox from '@/app/components/Checkbox';
import TolButton from '@/app/components/TolButton';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import FormDatePicker from '@/app/components/FormDatePicker';
import * as R from 'ramda';
import format from 'date-fns/format';
import { AppGetters } from '@/app/app.keys';
import {
  PermPlacementDetailsGetters,
  PermPlacementDetailsActions
} from '../perm-placement-details.keys';
import BillingMethodDisplay from '@/app/components/BillingMethodDisplay';
export default {
  name: 'PermPlacementDetailsForm',
  components: {
    DropdownSelect,
    InputField,
    TolList,
    TolRadioButton,
    FormDatePicker,
    Checkbox,
    TolButton,
    TolTypeAhead,
    BillingMethodDisplay
  },
  props: {
    permPlacement: {
      type: Object,
      required: true
    },
    isHistoryRecord: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {},
      isOnScreenDataModified: false,
      hadPaymentTerms: false,
      typeAhead: {
        ACCOUNT_MANAGER: false,
        RECRUITER: false,
        OVERRIDE: false
      },
      clientValidation: [],
      selectedClientPaymentTerm: null,
      selectedClientPaymentTermUnit: null
    };
  },
  computed: {
    accountManagerFieldHasError() {
      return (
        this.formErrors['ACCOUNT_MANAGER'] ||
        this.formErrors['account manager'] ||
        (this.formErrors['stakeholderCommissions'] &&
          this.formErrors['stakeholderCommissions'].includes(
            this.$t('assignment.labels.validation.accountManagerRequired')
          ))
      );
    },
    recruiterFieldHasError() {
      return (
        this.formErrors['RECRUITER'] ||
        this.formErrors['recruiter'] ||
        (this.formErrors['stakeholderCommissions'] &&
          this.formErrors['stakeholderCommissions'].includes(
            this.$t('assignment.labels.validation.recruiterRequired')
          ))
      );
    },
    currencies() {
      // TODO: implement AppGetters.CurrencyCodes
      //return this.$store.getters[AppGetters.CurrencyCodes];
      return ['CAD', 'USD', 'EUR'];
    },
    termUnits() {
      // TODO: implement AppGetters.TermUnits
      //return this.$store.getters[AppGetters.TermUnits];
      return ['N/A', 'DAYS', 'WEEKS', 'MONTHS'];
    },
    permPlacementAccountManagers() {
      return this.formData.stakeholderCommissions
        ? this.formData.stakeholderCommissions.filter(x => x.commission_type === 'ACCOUNT_MANAGER')
        : [];
    },
    permPlacementRecruiters() {
      return this.formData.stakeholderCommissions
        ? this.formData.stakeholderCommissions.filter(x => x.commission_type === 'RECRUITER')
        : [];
    },
    permPlacementCommissionOverrides() {
      return this.formData.stakeholderCommissions
        ? this.formData.stakeholderCommissions.filter(x => x.commission_type === 'OVERRIDE')
        : [];
    },
    branchNames() {
      return this.$store.getters[AppGetters.BranchNames];
    },
    divisionAbbrs() {
      return this.$store.getters[AppGetters.DivisionAbbrs];
    },
    startDate() {
      const dateParts = this.formData.start_date ? this.formData.start_date.split('-') : null;
      return dateParts ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2]) : null;
    },
    originalStartDate() {
      const dateParts = this.formData.original_start_date
        ? this.formData.original_start_date.split('-')
        : null;
      return dateParts ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2]) : null;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    splitDollars() {
      return this.formData.billing_amount - this.formData.outsource_amount;
    },
    splitPercentage() {
      return this.formData.billing_amount > 0
        ? ((this.splitDollars / this.formData.billing_amount) * 100).toFixed(2)
        : 0.0;
    },
    guaranteeAmount: {
      /**
      Show n/a only when With Replacement is no
       */
      get() {
        return this.formData.guarantee_replacement ? this.formData.guarantee_amount : 'N/A';
      },
      set(val) {
        this.formData.guarantee_amount = val || null;
      }
    },
    paymentTermAmount: {
      get() {
        return this.formData.payment_terms && this.formData.payment_terms !== ''
          ? this.formData.payment_terms
          : this.formData.payment_term_amount;
      },
      set(val) {
        //use the new payment_term_amout field if old payment_terms is currently null or empty
        if (this.formData.payment_terms) {
          this.formData.payment_terms = val;
        } else {
          this.formData.payment_term_amount = val;
        }
      }
    },
    guaranteeUnit() {
      return this.formData.guarantee_replacement ? this.formData.guarantee_unit : 'N/A';
    },
    paymentTermUnit() {
      return this.formData.payment_term_amount ? this.formData.payment_term_unit : 'N/A';
    },
    formErrors() {
      return this.$store.getters[
        PermPlacementDetailsGetters.GetPermPlacementDetailsValidationErrors
      ];
    },
    canThisUserSubmit() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['SUBMIT'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserProcess() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['PROCESS'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserReject() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['REJECT'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserCancel() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['CANCEL'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserObsolete() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['OBSOLETE'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserReopen() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['REOPEN'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserSave() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['PERM_PLACEMENT']['SAVE'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    }
  },
  watch: {
    permPlacement: {
      deep: true,
      immediate: true,
      handler(val) {
        this.formData = R.clone(this.permPlacement);
        this.isOnScreenDataModified = !this.clientValidation.length;
        this.hadPaymentTerms = val.payment_terms && val.payment_terms !== '';
        this.selectedClientPaymentTerm = val.payment_term_amount;
        this.selectedClientPaymentTermUnit = val.payment_term_unit;
      }
    },
    'formData.guarantee_replacement': {
      handler(val) {
        if (!val) {
          this.formData.guarantee_amount = null;
          this.formData.guarantee_unit = null;
        }
      }
    },
    'formData.guarantee_amount': {
      handler(val) {
        if (!val || val === '0') {
          this.formData.guarantee_unit = null;
        }
      }
    },
    'formData.payment_term_amount': {
      handler(val) {
        if (!val || val === '0') {
          this.formData.payment_term_unit = null;
        }
      }
    }
  },
  destroyed() {
    this.$store.dispatch(PermPlacementDetailsActions.ResetPermPlacementState);
  },
  methods: {
    doPermPlacementAction(action) {
      if (!this.currentUser.permissions['PERM_PLACEMENT'][action]) return;
      if (!this.isOnScreenDataModified && this.formData.reference_number && action === 'SAVE') {
        return;
      }
      const pastTenseAction = this.$options.filters.pasttense(action).toUpperCase();
      if (pastTenseAction === this.formData.status) {
        return;
      }
      if (!this.formData.reference_number && action !== 'SAVE') {
        return;
      }
      this.$store.dispatch(PermPlacementDetailsActions.DoPermPlacementAction, {
        permPlacement: this.formData,
        action
      });
      this.isOnScreenDataModified = false;
    },
    onStartDateChanged(evt) {
      if (evt) {
        const start = format(evt, 'YYYY-MM-DD');
        if (start < this.formData.original_start_date) {
          this.setClientValidation(
            'start_date',
            this.$t('permPlacement.details.errors.startDateBeforeOriginal')
          );
          this.formData.start_date = null;
        } else {
          this.formData.start_date = start;
        }
      }
    },
    onOriginalStartDateChanged(evt) {
      if (evt) {
        const originalStartDate = format(evt, 'YYYY-MM-DD');
        if (originalStartDate > this.formData.start_date) {
          this.setClientValidation(
            'original_start_date',
            this.$t('permPlacement.details.errors.originalDateAfterStart')
          );
          this.formData.original_start_date = null;
        } else {
          this.formData.original_start_date = originalStartDate;
        }
      }
    },
    toggleTypeAhead(key) {
      this.typeAhead[key] = !this.typeAhead[key];
    },
    onSelectSearchItem(event, key) {
      this.typeAhead[key] = false;

      const commissionUserObject = {
        assignment_id: this.assignmentId,
        commission_number: event.commission_number,
        commission_profile_id: event.commission_profile_id,
        commission_type: key,
        sort_order: 0,
        split: 0.0,
        tes_employee_id: event.tes_employee_id,
        display_name: event.display_name,
        email: event.email
      };

      if (!this.formData.stakeholderCommissions) {
        this.formData['stakeholderCommissions'] = new Array();
      }
      if (
        this.formData.stakeholderCommissions.findIndex(
          s =>
            s.commission_number === commissionUserObject.commission_number &&
            s.commission_type === commissionUserObject.commission_type
        ) === -1
      ) {
        this.formData.stakeholderCommissions.push(commissionUserObject);
      } else {
        const user = `${commissionUserObject.display_name}`;
        const commission = `#${commissionUserObject.commission_number}`;
        const msg = this.$t('permPlacement.details.errors.userAlreadyAdded', { user, commission });
        this.setClientValidation(key, msg);
      }
    },
    onSelectBillingMethodSearchItem(event, key) {
      if (key === 'BILLING_METHOD') {
        this.formData.billingMethod = event;
        this.formData.billing_method_id = event.id;
        this.formData = { ...this.formData };
      }
    },
    removeOnScreenValue(fieldName) {
      switch (fieldName) {
        case 'BILLING_METHOD':
          this.formData.billing_method_id = null;
          this.formData.billingMethod = null;
          break;
      }
    },
    setClientValidation(formField, message) {
      this.clientValidation.push({
        field: formField,
        message
      });
      window.setTimeout(() => {
        this.clientValidation = [];
      }, 4000);
    },
    clearClientValidation() {
      this.clientValidation = [];
    },
    hasErrorKey(key) {
      return this.clientValidation.filter(v => v.field === key).length;
    },
    getErrorKey(key) {
      return this.clientValidation.filter(v => v.field === key)[0].message;
    },
    removeCommissionUser(id, fieldName) {
      const updatedManagers = this.formData.stakeholderCommissions.filter(manager => {
        return !(manager.commission_profile_id === id && manager.commission_type === fieldName);
      });
      this.formData.stakeholderCommissions = updatedManagers;
    },
    updateRelatedValues(event, field) {
      const val = parseFloat(event);
      if (!isNaN(val)) {
        this.formData[field] = val;
        switch (field) {
          case 'salary':
            this.formData.billing_amount = (val * (this.formData.finders_percentage / 100)).toFixed(
              2
            );
            break;
          case 'finders_percentage':
            this.formData.billing_amount = (this.formData.salary * (val / 100)).toFixed(2);
            break;
          case 'billing_amount':
            this.formData.finders_percentage = ((val * 100) / this.formData.salary).toFixed(2);
            break;
        }
      }
    },
    checkPaymentTerms(event) {
      if (event) {
        this.formData.payment_term_amount = event;
      }
      if (
        this.formData.payment_term_amount >= 6 &&
        this.formData.payment_term_unit &&
        this.formData.payment_term_unit === 'MONTHS'
      ) {
        this.formData.guarantee_replacement = false;
        this.formData.guarantee_amount = null;
        this.formData.guarantee_unit = null;
      }
    },
    onClientSelect(event) {
      this.isOnScreenDataModified = true;
      this.formData.client_name = event.name;
      this.formData.client_id = event.id;
      //autopopulate payment terms and payment term units
      this.selectedClientPaymentTerm = event.payment_terms;
      this.selectedClientPaymentTermUnit = event.payment_term_unit;
    },
    disableFormElements(form) {
      for (let i = 0; i < form.elements.length; i++) {
        form.elements[i].disabled = true;
      }
      const tolTables = form.querySelectorAll('.tol-table');
      tolTables.forEach(tolTable => tolTable.classList.add('history'));

      const removeButtons = form.querySelectorAll('div.remove');
      removeButtons.forEach(removeButton => removeButton.classList.add('history'));

      const listHeaderButtons = form.querySelectorAll('.list-header span');
      listHeaderButtons.forEach(listHeaderButton => listHeaderButton.classList.add('history'));

      const checkboxes = form.querySelectorAll('.checkbox');
      checkboxes.forEach(box => box.classList.add('is-readonly'));

      const dropDowns = form.querySelectorAll('.dropdown-container');
      dropDowns.forEach(dropdown => dropdown.setAttribute('disabled', 'true'));
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.perm-placement-form {
  &.history {
    font-size: 0.8em;
    background-color: $light-grey-2;
    padding: 25px;
  }
  form {
    width: 100%;

    textarea {
      width: 430px;
    }

    section {
      margin-bottom: 20px;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .form-group {
        display: flex;
      }
      .form-control {
        padding: 10px;
        width: 450px;
        > label {
          display: block;
          font-weight: bold;
          margin-bottom: 5px;
        }
        &.empty {
          width: 50px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        &.sm {
          width: 200px;
        }
        &.error {
          background-color: $modified-form;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }

          .dropdown-container {
            flex-basis: 20%;
          }
        }

        .guarantee-term {
          width: 6em;
        }

        .tol-list ul li .remove {
          width: 30px;
          height: 30px;

          font-size: 1.2em;
          color: $row-border;
          text-align: center;
          float: right;
          &:hover {
            background-color: $row-border;
            border-radius: 100%;
            color: $white;
          }
        }

        .error-div {
          color: $red;
          background-color: $primary-error;
          padding: 8px;
          &:hover {
            cursor: pointer;
          }
          svg {
            margin-right: 15px;
          }
        }

        .grey {
          color: $grey;
        }
        &.currency {
          display: flex;
          flex-direction: column;
        }
        &.term {
          display: flex;
          align-items: flex-end;
        }
        &.term {
          width: 450px;
          .dropdown-container {
            width: 450px;
          }
        }
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }

      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }

      &.form-number-input {
        width: 100px;
      }

      &.form-currency-input {
        width: 75px;
      }
    }
    /* temporarily override the comment block at end of modules 
      will adopt for all comment blocks if/when style changes approved
    */
    .comment {
      background-color: $background-level-3;
      align-items: center;
    }
  }
}
.asterisk {
  color: red;
}
</style>
